import { Box, Grid, Tooltip } from '@mui/material';
import { emptyString } from 'constants/defaultValues';
import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PolicyRenewalItemCommentsComponent from './PolicyRenewalItemCommentsComponent';
import { Typography } from '@totalsoft/rocket-ui';
import ErrorIcon from '@mui/icons-material/Error';
import {
  RenewalItemAdditionalInfoButtonContainer,
  RenewalItemAdditionalInfoContainer,
  RenewalItemOfferLink,
  RenewalItemWarning
} from '../../PolicyRenewalStyles';

const PolicyRenewalItemGeneralDataComponent = ({ item }) => {
  const { t } = useTranslation();

  const [showAdditionalInfo, setShowAdditionalInfo] = useState(false);

  const composeId = useCallback(key => `offerList_offer_${item.documentId}${key ? `_${key}` : ''}`, [item]);

  return (
    <Box ml={5} sx={{ position: 'relative' }}>
      <Grid item container spacing={1} id={composeId('renewalPolicyDocument')} pb={1}>
        <Grid item xs={12} sm={6} md={3} lg={3} id={composeId('policyNumber')}>
          <strong>{t('Offer.PolicyRenewalList.Field.PolicyNumber')}</strong>: <span>{item.documentNumber}</span>
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3} id={composeId('period')}>
          <strong>{t('Offer.PolicyRenewalList.Field.Period')}</strong>: <span>{item.renewalPolicyDocument.period}</span>
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3} id={composeId('startDate')}>
          <strong>{t('Offer.PolicyRenewalList.Field.StartDate')}</strong>:{' '}
          <span>{t('DATE', { date: item.renewalPolicyDocument.startDate })}</span>
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3} id={composeId('finishDate')}>
          <strong>{t('Offer.PolicyRenewalList.Field.FinishDate')}</strong>:{' '}
          <span>{t('DATE', { date: item.renewalPolicyDocument.finishDate })}</span>
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3} id={composeId('AMR')}>
          <strong>{t('Offer.PolicyRenewalList.Field.AMR')}</strong>: <span>{item.renewalPolicyDocument.remainingDays}</span>
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3} id={composeId('amount')}>
          <strong>{t('Offer.PolicyRenewalList.Field.Amount')}</strong>:{' '}
          <span>{`${item.renewalPolicyDocument.amount} ${item.renewalPolicyDocument.currencyCode}`}</span>{' '}
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3} id={composeId('insurer')}>
          <strong>{t('Offer.PolicyRenewalList.Field.Insurer')}</strong>: <span>{item.renewalPolicyDocument.insurerName}</span>
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3} id={composeId('cascoInsurer')}>
          <strong>{t('Offer.PolicyRenewalList.Field.CascoInsurer')}</strong>: <span>{item.renewalPolicyDocument.cascoInsurerName}</span>
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3} id={composeId('insured')}>
          <strong>{t('Offer.PolicyRenewalList.Field.Insured')}</strong>: <span>{item.renewalPolicyDocument.insured.partnerName}</span>
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3} id={composeId('client')}>
          <strong>{t('Offer.PolicyRenewalList.Field.Client')}</strong>: <span>{item.renewalPolicyDocument.client.partnerName}</span>
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3} id={composeId('uniqueId')}>
          <strong>{t('Offer.PolicyRenewalList.Field.UniqueId')}</strong>: <span>{item.renewalPolicyDocument.client.uniqueID}</span>
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3} id={composeId('chassisNumber')}>
          <strong>{t('Offer.PolicyRenewalList.Field.ChassisNumber')}</strong>: <span>{item.renewalPolicyDocument.asset.chassisNumber}</span>
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3} id={composeId('brand')}>
          <strong>{t('Offer.PolicyRenewalList.Field.Brand')}</strong>: <span>{item.renewalPolicyDocument.asset.brand}</span>
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3} id={composeId('model')}>
          <strong>{t('Offer.PolicyRenewalList.Field.Model')}</strong>: <span>{item.renewalPolicyDocument.asset.model}</span>
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3} id={composeId('cylindricCapacity')}>
          <strong>{t('Offer.PolicyRenewalList.Field.CylindricCapacity')}</strong>:{' '}
          <span>{item.renewalPolicyDocument.asset.cylindricCapacity}</span>
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3} id={composeId('enginePower')}>
          <strong>{t('Offer.PolicyRenewalList.Field.EnginePower')}</strong>: <span>{item.renewalPolicyDocument.asset.enginePower}</span>
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3} id={composeId('seatsNumber')}>
          <strong>{t('Offer.PolicyRenewalList.Field.SeatsNumber')}</strong>: <span>{item.renewalPolicyDocument.asset.seatsNumber}</span>
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3} id={composeId('maxWeight')}>
          <strong>{t('Offer.PolicyRenewalList.Field.MaxWeight')}</strong>: <span>{item.renewalPolicyDocument.asset.maxWeight}</span>
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3} id={composeId('registrationNumber')}>
          <strong>{t('Offer.PolicyRenewalList.Field.RegistrationNumber')}</strong>:{' '}
          <span>{item.renewalPolicyDocument.asset.registrationNumber}</span>
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3} id={composeId('manufacturingYear')}>
          <strong>{t('Offer.PolicyRenewalList.Field.ManufacturingYear')}</strong>:{' '}
          <span>{item.renewalPolicyDocument.asset.manufacturingYear}</span>
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3} id={composeId('contractNumber')}>
          <strong>{t('Offer.PolicyRenewalList.Field.ContractNumber')}</strong>: <span>{item.renewalPolicyDocument.contractNumber}</span>
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3} id={composeId('contractState')}>
          <strong>{t('Offer.PolicyRenewalList.Field.ContractState')}</strong>: <span>{item.renewalPolicyDocument.contractState}</span>
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3} id={composeId('contractFinishDate')}>
          <strong>{t('Offer.PolicyRenewalList.Field.ContractFinishDate')}</strong>:{' '}
          <span>{t('DATE', { date: item.renewalPolicyDocument.contractFinishDate })}</span>
        </Grid>
        {item.documentId ? (
          <Grid item xs={12} sm={6} md={3} lg={3} id={composeId('offer')}>
            <strong> {t('Offer.PolicyRenewalList.Field.Offer')}</strong>:{' '}
            <RenewalItemOfferLink
              rel='noopener noreferrer'
              target='_blank'
              href={`/insurance-documents/${item.renewalPolicyDocument.documentId}`}
            >
              <span>{item.renewalPolicyDocument.documentNumber}</span>
            </RenewalItemOfferLink>
            {item.renewalPolicyDocument.documentState && <i>{` (${item.renewalPolicyDocument.documentState})`}</i>}
          </Grid>
        ) : (
          <Grid item xs={12} sm={6} md={3} lg={3} id={composeId('offer')}></Grid>
        )}
      </Grid>
      <Grid item container spacing={1} xs={12} mb={2}>
        <Grid item xs={12} md={6} id={composeId('warnings')}>
          <strong> {t('Offer.PolicyRenewalList.Field.Warnings')}</strong>:{' '}
          <RenewalItemWarning>{item.renewalPolicyDocument.warnings}</RenewalItemWarning>
        </Grid>
        <PolicyRenewalItemCommentsComponent
          composeId={composeId}
          documentId={item.renewalPolicyDocument.documentId}
          comments={item.renewalPolicyDocument.comments}
        />
      </Grid>
      {showAdditionalInfo && item.renewalPolicyDocument.additionalInfo && (
        <RenewalItemAdditionalInfoContainer id={composeId('additionalInfo')}>
          <Typography variant='caption'>{item.renewalPolicyDocument.additionalInfo}</Typography>
        </RenewalItemAdditionalInfoContainer>
      )}
      {item.renewalPolicyDocument.additionalInfo && (
        <RenewalItemAdditionalInfoButtonContainer>
          <Tooltip title={item.renewalPolicyDocument.additionalInfo || emptyString}>
            <ErrorIcon id={composeId('additionalInfo')} onClick={() => setShowAdditionalInfo(!showAdditionalInfo)} />
          </Tooltip>
        </RenewalItemAdditionalInfoButtonContainer>
      )}
    </Box>
  );
};

PolicyRenewalItemGeneralDataComponent.propTypes = {
  item: PropTypes.object.isRequired
};

export default PolicyRenewalItemGeneralDataComponent;
